import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PhoneIcon, XIcon } from 'lucide-react';
import type { Lead } from '../../types';
type Outcome = 'Connected' | 'No Answer' | 'Voicemail';
interface CallDialogProps {
  open: boolean;
  lead: Lead;
  onClose: () => void;
  onLog: (outcome: Outcome) => void;
}
const outcomes: {
  value: Outcome;
  help: string;
}[] = [{
  value: 'Connected',
  help: 'Spoke with the borrower'
}, {
  value: 'No Answer',
  help: 'No pickup, no voicemail left'
}, {
  value: 'Voicemail',
  help: 'Left a voicemail message'
}];
export function CallDialog({
  open,
  lead,
  onClose,
  onLog
}: CallDialogProps) {
  return <AnimatePresence data-id="element-137" data-mp="2r97m2op03q8r">
      {open ? <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Log call outcome" data-id="element-138" data-mp="2n9ypzd6hnga1">
          <motion.button type="button" aria-label="Close call dialog" className="absolute inset-0 bg-ink-900/25" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} data-id="element-139" data-mp="1buif4bhj9ijd" />
          <motion.div initial={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 8,
        scale: 0.98
      }} transition={{
        type: 'spring',
        stiffness: 340,
        damping: 30
      }} className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-lift" data-id="element-140" data-mp="olgaw5atmtp7">
            <button type="button" onClick={onClose} className="absolute right-4 top-4 rounded-lg p-2 text-ink-500 transition-colors hover:bg-ink-100" aria-label="Close" data-id="element-141" data-mp="jnek0y75lq36">
              <XIcon className="h-4 w-4" data-id="element-142" data-mp="3s25at8fwf55w" />
            </button>

            <div className="flex items-center gap-3" data-id="element-143" data-mp="3s3zw4nv30zai">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 text-emerald-700" data-id="element-144" data-mp="vvhesh1ke1o">
                <PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-145" data-mp="22ljeav4wgmh8" />
              </span>
              <div data-id="element-146" data-mp="1xt02a2lv1zej">
                <h2 className="text-base font-semibold text-ink-900" data-id="element-147" data-mp="1xshwae9ec6tf">Calling {lead.name}</h2>
                <p className="tabular text-sm text-ink-500" data-id="element-148" data-mp="1svhre6oa9a3w">{lead.phone}</p>
              </div>
            </div>

            <p className="mt-6 text-xs font-semibold uppercase tracking-wide text-ink-500" data-id="element-149" data-mp="1ov7mkxc88pqm">
              Log the outcome
            </p>
            <div className="mt-3 space-y-2" data-id="element-150" data-mp="2gksnhauougbp">
              {outcomes.map(outcome => <button key={outcome.value} type="button" onClick={() => onLog(outcome.value)} className="flex w-full items-center justify-between rounded-xl border border-ink-200 px-4 py-3 text-left transition-colors hover:border-brand-300 hover:bg-brand-50" data-id="element-151" data-mp="2cixoid08cf13">
                  <span data-id="element-152" data-mp="w34flg3att2o">
                    <span className="block text-sm font-medium text-ink-900" data-id="element-153" data-mp="w6sbun04otl3">{outcome.value}</span>
                    <span className="mt-0.5 block text-xs text-ink-500" data-id="element-154" data-mp="37py9f7mgcmqd">{outcome.help}</span>
                  </span>
                </button>)}
            </div>
          </motion.div>
        </div> : null}
    </AnimatePresence>;
}